import * as React from 'react';

import { Info } from 'lucide-react';

import loggingApis from '../api/services/logging.service';

const isLocalEnv = import.meta.env.MODE === 'development';
interface ErrorBoundaryProps {
  children: React.ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  retryKey: number;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError(error: any): { hasError: boolean } {
    // Update state so the next render will show the fallback UI.
    console.error(error);
    return { hasError: true };
  }

  private handleReload = (): void => {
    this.setState({ hasError: false });
    this.setState(prevState => ({
      retryKey: prevState.retryKey + 1,
    }));
  };

  private savelog = async (error: any): Promise<void> => {
    console.error('[Runtime Error]', error);
    if (!isLocalEnv) {
      await loggingApis.saveLog({
        type: error.type,
        message: error.message,
        config: JSON.stringify({
          source: error.source,
          line: error.line,
          column: error.column,
          time: error.time,
          componentPath: error.stack,
        }),
        trace: error.stack || '',
      });
    }
  };

  componentDidCatch(error: any, info: any): void {
    this.setState({ hasError: true });

    const errorPayload = {
      type: 'RuntimeError',
      message: error.message,
      source: window.location.href,
      line: info.lineNumber || 'No line number available',
      column: info.columnNumber || 'No column number available',
      stack: info.componentStack || 'No stack trace available',
      time: new Date().toISOString(),
    };
    this.savelog(errorPayload);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className='flex flex-col items-center justify-center absolute h-full w-full p-6 bg-primary-100 rounded-xl border border-primary-200 shadow-sm text-center space-y-4'>
          <Info className='w-10 h-10 text-primary-400' />
          <h2 className='text-lg font-semibold text-primary-600'>Oops! Something went wrong.</h2>
          <p className='text-sm text-primary-600 max-w-md'>
            An unexpected error occurred in this component. Please try reloading it.
          </p>
          <button
            onClick={this.handleReload}
            className='px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-all'
          >
            Reload Component
          </button>
        </div>
      );
    }
    return <div key={this.state.retryKey}>{this.props.children}</div>;
  }
}

export default ErrorBoundary;
